import React, { useState, useEffect } from 'react';
import './ScrollDebug.css';

/**
 * Debug panel to diagnose scrolling issues
 * Add ?scrollDebug to URL to show this panel
 */
function ScrollDebug() {
  const [debug, setDebug] = useState({});
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    // Check if debug mode is enabled via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    setShowDebug(urlParams.has('scrollDebug'));

    if (!urlParams.has('scrollDebug')) return;

    const updateDebugInfo = () => {
      const html = document.documentElement;
      const body = document.body;
      const root = document.getElementById('root');
      const app = document.querySelector('.App');
      const aboutContainer = document.querySelector('.about-container');

      const getComputedStyles = (element, name) => {
        if (!element) return { name, error: 'Element not found' };
        const computed = window.getComputedStyle(element);
        return {
          name,
          height: computed.height,
          minHeight: computed.minHeight,
          maxHeight: computed.maxHeight,
          overflow: computed.overflow,
          overflowY: computed.overflowY,
          overflowX: computed.overflowX,
          position: computed.position,
          scrollHeight: element.scrollHeight + 'px',
          clientHeight: element.clientHeight + 'px',
          canScroll: element.scrollHeight > element.clientHeight,
        };
      };

      setDebug({
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          scrollY: window.scrollY,
          maxScrollY: Math.max(0, document.documentElement.scrollHeight - window.innerHeight),
        },
        elements: [
          getComputedStyles(html, 'html'),
          getComputedStyles(body, 'body'),
          getComputedStyles(root, '#root'),
          getComputedStyles(app, '.App'),
          getComputedStyles(aboutContainer, '.about-container'),
        ],
      });
    };

    updateDebugInfo();
    window.addEventListener('scroll', updateDebugInfo);
    window.addEventListener('resize', updateDebugInfo);

    const interval = setInterval(updateDebugInfo, 1000);

    return () => {
      window.removeEventListener('scroll', updateDebugInfo);
      window.removeEventListener('resize', updateDebugInfo);
      clearInterval(interval);
    };
  }, []);

  if (!showDebug) return null;

  return (
    <div className="scroll-debug-panel">
      <div className="scroll-debug-header">
        <h3>Scroll Debug Panel</h3>
        <button onClick={() => setShowDebug(false)}>Close</button>
      </div>

      <div className="scroll-debug-section">
        <h4>Viewport</h4>
        <div className="debug-info">
          <div>Size: {debug.viewport?.width}x{debug.viewport?.height}</div>
          <div>Scroll Y: {debug.viewport?.scrollY} / {debug.viewport?.maxScrollY}</div>
          <div className={debug.viewport?.maxScrollY > 0 ? 'debug-ok' : 'debug-error'}>
            {debug.viewport?.maxScrollY > 0 ? '✓ Page is scrollable' : '✗ Page NOT scrollable'}
          </div>
        </div>
      </div>

      <div className="scroll-debug-section">
        <h4>Element Styles</h4>
        {debug.elements?.map((el, idx) => (
          <details key={idx} className="debug-element" open={!el.canScroll}>
            <summary className={el.canScroll ? 'debug-ok' : 'debug-error'}>
              {el.name} {el.canScroll ? '✓' : '✗'} {el.error ? '(not found)' : ''}
            </summary>
            {!el.error && (
              <div className="debug-details">
                <div>height: {el.height}</div>
                <div>min-height: {el.minHeight}</div>
                <div>max-height: {el.maxHeight}</div>
                <div className={el.overflowY === 'auto' || el.overflowY === 'scroll' ? 'debug-ok' : 'debug-warn'}>
                  overflow-y: {el.overflowY}
                </div>
                <div>position: {el.position}</div>
                <div>scrollHeight: {el.scrollHeight}</div>
                <div>clientHeight: {el.clientHeight}</div>
                <div className={el.canScroll ? 'debug-ok' : 'debug-error'}>
                  {el.canScroll ? '✓ Can scroll' : '✗ Cannot scroll'}
                </div>
              </div>
            )}
          </details>
        ))}
      </div>

      <div className="scroll-debug-section">
        <h4>Quick Fixes</h4>
        <button onClick={() => {
          document.documentElement.style.overflowY = 'auto';
          document.documentElement.style.height = 'auto';
        }}>
          Fix html overflow
        </button>
        <button onClick={() => {
          document.body.style.overflowY = 'auto';
          document.body.style.height = 'auto';
        }}>
          Fix body overflow
        </button>
        <button onClick={() => {
          const root = document.getElementById('root');
          if (root) {
            root.style.overflowY = 'auto';
            root.style.height = 'auto';
          }
        }}>
          Fix #root overflow
        </button>
      </div>
    </div>
  );
}

export default ScrollDebug;
